/**
 * New document / new revision (mockup v7) - no "publish" anywhere. Upload goes
 * straight to S3 with a presigned URL; the API records a fingerprint (SHA-256)
 * and nothing is published until an approver signs off.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, putFile, sha256, type DocDetail } from "../lib/api";
import { useStore } from "../lib/store";
import { AREA_LABELS, AREA_CODES, TYPE_COLORS, TYPE_LABELS, TYPE_NAMES, TYPE_CODES, VIDEO_TYPE_CODES, VIDEO_EXTENSIONS, MAX_FILE_BYTES, MAX_VIDEO_BYTES, isVideoFile, coName,
  CLASSIFICATION_CODES, CLASSIFICATION_LABELS, RETENTION_CODES, RETENTION_LABELS, VISIBILITIES, VISIBILITY_LABELS, RESTRICTED, BUMPS, BUMP_LABELS, fmtVersion, nextVersion, type Bump } from "../lib/brand";
import { bytes, plusDays } from "../lib/format";
import { Co, Loading, TypeBadge } from "../components/bits";
import { FrameworkPicker, FrameworkTags } from "../components/FrameworkPicker";
import { extractTitle, guessType, type TitleSource } from "../lib/extract";
import { SeeHow } from "../components/SeeHow";

const SOURCE_TEXT: Record<TitleSource, string> = {
  properties: "the document's Title property",
  heading: "the document's heading",
  "first line": "the first line of the document",
  "file name": "the file name",
};

const ACCEPT = ".docx,.doc,.docm,.dotx,.xlsx,.xls,.xlsm,.pptx,.ppt,.vsdx,.vsd,.pdf,.odt,.ods,.odp";
/** One line per type, to help authors pick (ADR-0006 / ADR-0012). */
const TYPE_HINT: Record<string, string> = {
  POL: "Rules everyone must follow", STD: "Required settings and specs", PLN: "What we do when it happens",
  PRO: "How work flows between teams", SOP: "Step by step, who does what",
  HOW: "Quick guide, document or video", GUI: "Recommended good practice", FRM: "A form people fill in", TMP: "An approved starting point",
};
/** The everyday types get tiles; every other active type is in "More types" (ADR-0016). */
const COMMON_TYPES = ["POL", "SOP", "HOW", "STD", "PRO", "PLN", "FRM", "GUI"];
/** How-Tos and SOPs may also be a video (ADR-0012). */
const ACCEPT_VIDEO = ACCEPT + "," + VIDEO_EXTENSIONS.map((e) => "." + e).join(",");

export function NewDocumentPage() {
  const { id } = useParams();
  const { me, p, ws, toast, reload } = useStore();
  const nav = useNavigate();
  const [existing, setExisting] = useState<DocDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const cos = [...p.myCodes.filter((c) => p.canAuthor(c)), ...(p.canAuthor("CPY") ? ["CPY"] : [])];
  const defCo = ws !== "ALL" && p.canAuthor(ws) ? ws : cos.find((c) => c !== "CPY") ?? cos[0] ?? "";
  const [co, setCo] = useState(defCo);
  const [type, setType] = useState<string>("POL");
  const [area, setArea] = useState<string>("SEC");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  // An accepted suggestion (Suggest a change) arrives as ?notes=, so it goes in the revision history.
  const [qs] = useSearchParams();
  const [notes, setNotes] = useState(() => qs.get("notes") ?? "");
  // Default first review: the company's review cycle (ADR-0015), 12 months if unset.
  const cycleFor = (code: string) => me.companies.find((c) => c.code === code)?.review_months ?? 12;
  const [review, setReview] = useState(plusDays(Math.round(cycleFor(defCo) * 30.44)));
  const [reviewTouched, setReviewTouched] = useState(false);
  useEffect(() => { if (!reviewTouched) setReview(plusDays(Math.round(cycleFor(co) * 30.44))); }, [co]); // eslint-disable-line react-hooks/exhaustive-deps
  const [approver, setApprover] = useState("");
  const [approvers, setApprovers] = useState<{ id: string; name: string }[]>([]);
  const [num, setNum] = useState("");
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [cls, setCls] = useState("INT");
  const [retention, setRetention] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [bump, setBump] = useState<Bump>("minor");
  const [file, setFile] = useState<File | null>(null);
  const [over, setOver] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [pct, setPct] = useState(0);
  // Upload-first (2026-10-19): what we filled in from the file, and what the author has changed by hand.
  const [touched, setTouched] = useState({ title: false, type: false });
  const [filled, setFilled] = useState<{ source: TitleSource; type: string | null; reading: boolean } | null>(null);
  const pickSeq = useRef(0);
  const touchedRef = useRef(touched);
  touchedRef.current = touched;
  const fi = useRef<HTMLInputElement>(null);

  // New revision: load the document.
  useEffect(() => {
    if (!id) return;
    api.document(id).then((d) => { setExisting(d); setCo(d.document.company); if (d.document.pending?.state !== "in_review" && !qs.get("notes")) setNotes(""); }, (e: Error) => setLoadErr(e.message));
  }, [id]);

  // Number preview + approver list (new document only for the number).
  useEffect(() => {
    if (!co) return;
    let live = true;
    api.approversFor(co).then((r) => live && setApprovers(r.approvers), () => live && setApprovers([]));
    if (!id) {
      const t = setTimeout(() => api.nextNumber(co, type, area).then((r) => live && setNum(r.base_number), () => live && setNum("")), 150);
      return () => { live = false; clearTimeout(t); };
    }
    return () => { live = false; };
  }, [co, type, area, id]);

  if (!cos.length) return <div className="card empty view"><h3>Not found</h3>Only authors can create documents.</div>;
  if (id && loadErr) return <div className="card empty view"><h3>Not found</h3>{loadErr}</div>;
  if (id && !existing) return <Loading />;

  const d = existing?.document;
  const pendMine = d?.pending && existing?.permissions.is_uploader ? d.pending : null;
  if (d && !existing!.permissions.can_author) return <div className="card empty view"><h3>Not found</h3>Only authors for {coName(d.company)} can upload revisions.</div>;
  if (d?.pending && !pendMine) return <div className="card empty view"><h3>A revision is already in progress</h3>{d.pending.by.name} is working on the next version. It needs to be approved or withdrawn first.</div>;
  if (pendMine?.state === "in_review") return <div className="card empty view"><h3>Waiting for approval</h3>Withdraw the submission on the document page before replacing the file.</div>;

  const curV = d?.version ? (d.version.split(".").map(Number) as [number, number, number]) : null;
  const nextV = fmtVersion(nextVersion(curV, curV ? bump : null))!;
  const docType = d?.type ?? type;
  const videoOk = VIDEO_TYPE_CODES.includes(docType);
  const fileIsVideo = !!file && isVideoFile(file.type, file.name);
  const pickFile = (f: File | undefined) => {
    if (!f) return;
    const video = isVideoFile(f.type, f.name);
    // A new document can start from a video: it becomes a How-To unless the author already picked SOP.
    if (video && !videoOk && d) { setErr(`Videos can be How-Tos or SOPs. Upload a document instead.`); return; }
    if (video && !videoOk && touched.type) { setErr(`Videos can be How-Tos or SOPs. Change the type, or upload a document.`); return; }
    if (f.size > (video ? MAX_VIDEO_BYTES : MAX_FILE_BYTES)) { setErr(video ? "Videos are limited to 1 GB." : "Files are limited to 100 MB."); return; }
    setErr(""); setFile(f);
    if (d) return;
    const seq = ++pickSeq.current;
    setFilled({ source: "file name", type: null, reading: true });
    void extractTitle(f).then((x) => {
      if (seq !== pickSeq.current) return; // a newer file was chosen meanwhile
      const now = touchedRef.current; // the author may have typed while we were reading
      const guess = video ? (type === "SOP" ? "SOP" : "HOW") : guessType(x.title, f.name);
      if (!now.title) setTitle(x.title);
      if (!now.type && guess) setType(guess);
      setFilled({ source: x.source, type: !now.type ? guess : null, reading: false });
    });
  };

  const save = async (submit: boolean) => {
    setErr("");
    if (!d && !title.trim()) return setErr("Give the document a title.");
    if (!file) return setErr("Choose a file.");
    if (fileIsVideo && !videoOk) return setErr("Videos can be How-Tos or SOPs. Change the type, or upload a document.");
    if (d && d.revision && !notes.trim() && !pendMine) return setErr("Say what changed; it goes in the revision history.");
    let docId = d?.id ?? "";
    let label = d?.base_number ?? "";
    try {
      setBusy("Preparing…");
      const meta = { file_name: file.name, content_type: file.type || "application/octet-stream", file_size: file.size, notes: notes.trim() || undefined };
      let created: { version: { id: string }; upload: { url: string } };
      if (d) created = await api.newRevision(d.id, meta);
      else {
        const c = await api.createDocument({ company: co, type, area, title: title.trim(), description: desc.trim() || undefined, review_date: review || undefined, frameworks,
          classification: cls, retention: retention || null, visibility, ...meta });
        docId = c.document.id; label = c.document.base_number; created = c;
      }
      const vid = created.version.id;
      setBusy("Uploading…");
      await putFile(created.upload.url, file, setPct);
      setBusy("Checking the file…");
      await api.confirmUpload(vid, await sha256(file));
      if (submit) {
        setBusy("Submitting…");
        await api.submit(vid, approver || undefined, false, curV ? bump : undefined);
      }
      toast(submit ? `Submitted ${label} v${nextV} for approval. It publishes when an approver signs off.` : `Draft saved: ${label}.`);
      await reload();
      nav(`/doc/${docId}`);
    } catch (e) {
      setBusy(null);
      setErr((e as Error).message);
      if (docId && !d) { await reload(); toast("The document was created but the upload didn't finish. Upload the file again from its page."); nav(`/doc/${docId}`); }
    }
  };

  const approverChoices = approvers.filter((a) => a.id !== me.user.id);
  const ext = file ? (file.name.split(".").pop() ?? "").toUpperCase().slice(0, 4) : "";
  const previewTitle = (d ? d.title : title.trim()) || "Your document title";
  const previewNum = d ? `${d.base_number} v${nextV}` : `${num || `${co}-${type}-${area}-…`} v1.0.0`;
  const approverName = approver ? approverChoices.find((a) => a.id === approver)?.name : null;
  const chooseType = (k: string) => { setType(k); setTouched((t) => ({ ...t, type: true })); };

  const uploadSection = (
    <section className="nd-sec">
      <header><b>1</b><div><h3>{d ? "Upload the new file" : "Start with the file"}</h3>
        <p>{d ? (videoOk ? "A document, or a video for How-Tos and SOPs." : "Word, Excel, PowerPoint, Visio or PDF.")
          : "We'll read the title from it and suggest a type. You can change both."}</p></div></header>
      <div className={`drop nd-drop ${over ? "over" : ""} ${file ? "has" : ""}`} tabIndex={0} role="button"
        onClick={() => fi.current?.click()} onKeyDown={(e) => { if (e.key === "Enter") fi.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pickFile(e.dataTransfer.files[0]); }}>
        {file ? (
          <div className="nd-file">
            <span className={`fext ${fileIsVideo ? "vid" : ""}`}>{fileIsVideo ? "▶" : ext}</span>
            <div><b>{file.name}</b><div className="help">{bytes(file.size)} · {fileIsVideo ? "played in DocVault's video player" : /\.pdf$/i.test(file.name) ? "shown to readers as it is" : "a PDF copy is made for reviewers and readers"} · click to change</div></div>
          </div>
        ) : (
          <div className="nd-empty">
            <span className="nd-up">↑</span>
            <div><b>Drop a file here</b> or click to choose
              <div className="help">Word, Excel, PowerPoint, Visio or PDF up to 100 MB{!d || videoOk ? <> · How-Tos and SOPs can also be a video (.mp4, .mov, .m4v, .webm) up to 1 GB</> : null}</div></div>
          </div>
        )}
      </div>
      <input type="file" ref={fi} hidden accept={!d || videoOk ? ACCEPT_VIDEO : ACCEPT} onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
      {busy === "Uploading…" && <div className="progress"><span style={{ width: `${pct}%` }} /></div>}
    </section>
  );

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">{d ? (pendMine ? `Replace file · v${nextV}` : "New version") : "New document"}</div>
          <h1 className="title">{d ? d.title : <>Start a <em>controlled</em> document</>}</h1>
          <div className="lede">{d ? "Upload the new file and say what changed. The current version stays live until this one is approved." : "Drop in the file and we'll fill in what we can. Nothing is published until an approver signs off."}</div>
          <div className="lede-help"><SeeHow video={d ? "revision" : "new-document"} /></div>
        </div>
      </div>
      <div className="formgrid nd">
        <form className="card nd-form" noValidate onSubmit={(e) => { e.preventDefault(); void save(true); }}>
          {d && (
            <div className="note info nd-note">
              {d.revision
                ? <>Current version is <b className="mono">{d.number}</b>. Your upload becomes <b className="mono">v{nextV}</b> once an approver signs off.</>
                : <>This document hasn't been approved yet. Your upload replaces the draft file; it becomes <b className="mono">v1.0.0</b> on approval.</>}
            </div>
          )}
          {uploadSection}
          {!d && (
            <>
              <section className="nd-sec">
                <header><b>2</b><div><h3>What kind of document?</h3><p>The type and area make up the document number.</p></div>
                  {filled?.type && !touched.type && <span className="nd-auto">{fileIsVideo ? "Videos are How-Tos or SOPs" : "Suggested from the title"}</span>}</header>
                <div className="type-grid" role="radiogroup" aria-label="Document type">
                  {COMMON_TYPES.filter((k) => TYPE_CODES.includes(k)).map((k) => {
                    const blocked = fileIsVideo && !VIDEO_TYPE_CODES.includes(k);
                    return (
                      <button type="button" key={k} role="radio" aria-checked={type === k} disabled={blocked}
                        title={blocked ? "Videos can be How-Tos or SOPs" : undefined}
                        className={`type-tile ${type === k ? "on" : ""}`}
                        style={{ ["--tc" as string]: TYPE_COLORS[k] }} onClick={() => chooseType(k)}>
                        <TypeBadge type={k} sm />
                        <span><b>{TYPE_LABELS[k]}</b><small>{TYPE_HINT[k]}</small></span>
                      </button>
                    );
                  })}
                </div>
                <label className="nd-more"><span className="lbl">More types</span>
                  <select className={`inp ${!COMMON_TYPES.includes(type) ? "inp-auto" : ""}`} value={COMMON_TYPES.includes(type) ? "" : type} onChange={(e) => e.target.value && chooseType(e.target.value)}>
                    <option value="">Checklist, Report, Assessment, KB article, Diagram…</option>
                    {TYPE_CODES.filter((k) => !COMMON_TYPES.includes(k)).map((k) => (
                      <option key={k} value={k} disabled={fileIsVideo && !VIDEO_TYPE_CODES.includes(k)}>{k} - {TYPE_NAMES[k] ?? k}</option>
                    ))}
                  </select></label>
                <div className="g2">
                  <label><span className="lbl">Company <span className="req">*</span></span>
                    <select className="inp" value={co} onChange={(e) => { setCo(e.target.value); setApprover(""); }}>{cos.map((c) => <option key={c} value={c}>{c} - {coName(c)}</option>)}</select></label>
                  <label><span className="lbl">Area <span className="req">*</span></span>
                    <select className="inp" value={area} onChange={(e) => setArea(e.target.value)}>{AREA_CODES.map((k) => <option key={k} value={k}>{k} - {AREA_LABELS[k] ?? k}</option>)}</select></label>
                </div>
              </section>
              <section className="nd-sec">
                <header><b>3</b><div><h3>Describe it</h3><p>What people will see in the library and in search.</p></div></header>
                <label><span className="lbl">Title <span className="req">*</span></span>
                  <input className={`inp ${filled && !touched.title && !filled.reading && title ? "inp-auto" : ""}`} value={title}
                    onChange={(e) => { setTitle(e.target.value); setTouched((t) => ({ ...t, title: true })); }}
                    placeholder={filled?.reading ? "Reading the file…" : "e.g., Remote Work Policy"} maxLength={200} />
                  {filled && !touched.title && (
                    <span className="help nd-filled">{filled.reading ? "Reading the title from the file…" : <>Filled in from {SOURCE_TEXT[filled.source]}. Edit it if it isn't right.</>}</span>
                  )}
                </label>
                <label><span className="lbl">Description</span><textarea className="inp" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="One line on what this covers and who it applies to" /></label>
                <div>
                  <span className="lbl">Frameworks <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional · lets people filter, e.g. everything for CMMC)</span></span>
                  <FrameworkPicker value={frameworks} onChange={setFrameworks} />
                </div>
                <div className="g3">
                  <label><span className="lbl">Classification</span>
                    <select className="inp" value={cls} onChange={(e) => setCls(e.target.value)}>
                      {CLASSIFICATION_CODES.map((k) => <option key={k} value={k}>{CLASSIFICATION_LABELS[k] ?? k}</option>)}
                    </select></label>
                  <label><span className="lbl">Visibility</span>
                    <select className="inp" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                      {VISIBILITIES.map((k) => <option key={k} value={k}>{VISIBILITY_LABELS[k]}</option>)}
                    </select></label>
                  <label><span className="lbl">Retention</span>
                    <select className="inp" value={retention} onChange={(e) => setRetention(e.target.value)}>
                      <option value="">Not set</option>
                      {RETENTION_CODES.map((k) => <option key={k} value={k}>{RETENTION_LABELS[k] ?? k}</option>)}
                    </select></label>
                </div>
                {cls === RESTRICTED && <div className="help">Restricted: only authors, reviewers, approvers and admins can read it until you name other readers on its page.</div>}
                {visibility !== "internal" && <div className="help">Client shared and Public take effect when client access arrives (Phase 3).</div>}
              </section>
            </>
          )}
          <section className="nd-sec">
            <header><b>{d ? 2 : 4}</b><div><h3>Review and approval</h3><p>Who signs it off, and when it's next checked.</p></div></header>
            <div className={d ? "" : "g2"}>
              {!d && <label><span className="lbl">Next review due</span><input className="inp" type="date" value={review} onChange={(e) => { setReview(e.target.value); setReviewTouched(true); }} /></label>}
              <label><span className="lbl">Send to</span>
                <select className="inp" value={approver} onChange={(e) => setApprover(e.target.value)}>
                  <option value="">Any approver at {coName(co)}</option>
                  {approverChoices.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></label>
            </div>
            {curV && (
              <fieldset className="bump">
                <legend className="lbl">Size of the change</legend>
                {BUMPS.map((b) => (
                  <label key={b} className={`bump-opt ${bump === b ? "on" : ""}`}>
                    <input type="radio" name="bump" checked={bump === b} onChange={() => setBump(b)} />
                    <span><b className="mono">v{fmtVersion(nextVersion(curV, b))}</b> {BUMP_LABELS[b]}</span>
                  </label>
                ))}
              </fieldset>
            )}
            <label><span className="lbl">{d?.revision ? "What changed" : "Notes for the approver"} {d?.revision && !pendMine && <span className="req">*</span>}</span>
              <textarea className="inp" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder={d?.revision ? "Summarize the change; this goes in the revision history" : "Anything the approver should look at closely"} /></label>
          </section>
          <div className="err" role="alert">{err}</div>
          <div className="nd-actions">
            {busy && <span className="spin">{busy}</span>}
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost" onClick={() => nav(-1)} disabled={!!busy}>Cancel</button>
            <button type="button" className="btn btn-ghost" onClick={() => void save(false)} disabled={!!busy}>Save draft</button>
            <button type="submit" className="btn btn-accent" disabled={!!busy}>Submit for approval</button>
          </div>
        </form>
        <aside className="nd-side">
          <div className="card nd-preview">
            <div className="eyebrow">In the library</div>
            <div className="docrow">
              <TypeBadge type={docType} />
              <div className="meta">
                <div className="t1" style={{ color: d || title.trim() ? undefined : "var(--faint)" }}>{previewTitle}</div>
                <div className="sub"><span className="docno">{previewNum}</span><Co code={d?.company ?? co} /></div>
              </div>
            </div>
            {(d?.frameworks ?? frameworks).length > 0 && <div style={{ marginTop: 10 }}><FrameworkTags tags={d?.frameworks ?? frameworks} /></div>}
            <dl className="nd-facts">
              <dt>Type</dt><dd>{TYPE_LABELS[docType as keyof typeof TYPE_LABELS]}</dd>
              <dt>Area</dt><dd>{AREA_LABELS[(d?.area ?? area) as keyof typeof AREA_LABELS]}</dd>
              <dt>Approver</dt><dd>{approverName ?? `Any approver at ${coName(co)}`}</dd>
              {!d && <><dt>Review due</dt><dd>{review || "-"}</dd></>}
              <dt>File</dt><dd>{file ? (fileIsVideo ? "Video" : ext ? `${ext} file` : "File") : "Not chosen yet"}</dd>
            </dl>
            <div className="help" style={{ margin: "10px 0 0" }}>The document ID is assigned when you save and never changes. The version (v{nextV}) is set when it's approved.</div>
          </div>
          <div className="card flow">
            <div className="eyebrow">How publishing works</div>
            <div className="fstep cur"><b>1</b><div>You upload and submit<small>Saved as a draft until you submit.</small></div></div>
            <div className="fstep"><b>2</b><div>An approver reviews<small>They see exactly what readers will see.</small></div></div>
            <div className="fstep"><b>3</b><div>Approved = published<small>Version number, PDF copy and a file fingerprint are recorded. The approver can set a later effective date.</small></div></div>
            <div className="note warn" style={{ margin: "4px 0 0", display: "block" }}>If you're your company's only approver you approve it yourself, and the record says <b>Self-approved</b>.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
